<template>
  <v-dialog
    v-model="localDialog"
    width="35%"
    @update:modelValue="updateDialog"
    transition=""
    persistent
    :open-delay="0"
    :close-delay="0"
  >
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Add Port" : "Update Port" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="PortSetup">
          <v-row>
            <v-col cols="12">
              <v-text-field
                label="Name"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="name.value.value"
                :error-messages="name.errorMessage.value"
                clearable
              >
                <template #label>
                  Name <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>

              <v-autocomplete
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="country_id.value.value"
                :error-messages="country_id.errorMessage.value"
                clearable
                :items="getInvCountryList"
                :item-title="(item) => `${item.name}`"
                autocomplete="off"
                item-value="id"
                :menu-props="{ width: 300 }"
                @update:modelValue="getInvCountryList"
              >
                <template #label>
                  Country
                  <span class="text-red-accent-4">*</span>
                </template>
                <template v-slot:selection="{ props, item }">
                    <div class="d-flex align-center noto-color-emoji-regular"
                        v-bind="props"
                        color="primary"
                        variant="tonal"
                    >
                    <span class="mr-1">{{ item.raw.emoji }}</span>
                    {{ item.raw.name }}
                  </div>
                </template>
                <template v-slot:item="{ props, item }">
                  <v-list-item v-bind="props" v-slot=""  :title="null">
                      <div class="d-flex align-center noto-color-emoji-regular" style="width: 100%;">
                          <span class="mr-2 text-lg">{{ item.raw.emoji }}</span>
                          <div class="flex-grow-1">
                              <span>{{ item.raw.name }}</span>
                              <small v-if="item.raw.native" class="text-grey">
                                  ({{ item.raw.native }})
                              </small>
                          </div>
                          <div class="text-grey text-caption">
                              +{{ item.raw.phonecode }}
                          </div>
                      </div>
                  </v-list-item>
                </template>
              </v-autocomplete>

              <v-text-field
                label="Remarks (Optional)"
                density="compact"
                variant="outlined"
                bg-color="grey-lighten-4"
                v-model="remarks.value.value"
                :error-messages="remarks.errorMessage.value"
                clearable
              ></v-text-field>

              <v-checkbox
                label="Active"
                v-model="status.value.value"
                :error-messages="status.errorMessage.value"
                density="compact"
                variant="outlined"
                hide-details
                single-line
                color="primary"
                value="1"
              ></v-checkbox>
              <input v-model="port_id.value.value" type="hidden" />
            </v-col>
          </v-row>

          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn
                class="bg-red"
                variant="flat"
                type="reset"
                @click="closeDialog"
                >Close</v-btn
              >
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import { fileServerUrl } from "@/store/api";

export default {
  name: "PortSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        name: "",
        remarks: "",
        status: "1",
        port_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
    };
  },
  computed: {
    getInvCountryList() {
      let country = this.$store.getters.getInvCountryList;
      if (country.length > 0) {
        return country;
      }
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.name.setValue(newData.name || "", false);
        this.country_id.setValue(newData.country_id || "", false);
        this.remarks.setValue(newData.remarks || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.port_id.setValue(newData.id || "", false);
      },
    },
  },
  created() {
    if (this.$store.getters.getInvCountryList.length == 0) {
      this.loading = true;
      this.$store.dispatch("getInvCountryList").then((response) => {
        this.loading = false;
      });
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        name: yup.string().min(1).max(50).required().trim().label("Name"),
        country_id: yup.number().positive().integer().required().label("Country"),
        remarks: yup.string().max(250).nullable().trim().label("Remarks"),
      }),
    });

    const name = useField("name");
    const country_id = useField("country_id");
    const remarks = useField("remarks");
    const status = useField("status");
    const port_id = useField("port_id");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
      store
        .dispatch("saveUpdatePort", values)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getPortList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      remarks,
      name,
      country_id,
      status,
      errorMessages,
      port_id,
      resetForm,
      loading,
    };
  },
  mounted() {},
};
</script>
